import { useEffect, useRef } from "react";

/**
 * useMouseParallax
 *
 * Writes normalised mouse position (-1..1 on each axis) into CSS custom
 * properties --mx and --my on the returned ref'd element. CSS can then use
 * them to drive transforms (orbs, tilts, spotlights) without React re-renders.
 *
 * Disabled for prefers-reduced-motion and for touch-only devices.
 */
export default function useMouseParallax({ strength = 1 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarsePointer =
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (prefersReduced || coarsePointer) return;

    let ticking = false;
    let lastEvent = null;

    const update = () => {
      ticking = false;
      if (!lastEvent) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const mx = ((lastEvent.clientX - cx) / (rect.width / 2)) * strength;
      const my = ((lastEvent.clientY - cy) / (rect.height / 2)) * strength;
      const clamp = (v) => Math.max(-1, Math.min(1, v));
      el.style.setProperty("--mx", clamp(mx).toFixed(3));
      el.style.setProperty("--my", clamp(my).toFixed(3));
    };

    const onMove = (e) => {
      lastEvent = e;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    const onLeave = () => {
      el.style.setProperty("--mx", "0");
      el.style.setProperty("--my", "0");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, [strength]);

  return ref;
}
